import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AxiosInstance } from 'axios';

import { errorMessageAdapter } from '../../../adapters';
import { useDialogsStore } from './store/dialogs.store';
import { useApi } from '../../../services/hooks/apiHook';
import { useAuth } from '../../../store/auth';

import { ManagementService } from '../../../services/ManagementService';
import { FolderService } from '../../../services/FolderService';
import { CompanyDocuments, Shareholder } from '../../../services/Interfaces/IManagemenntService';
import { Documents, ICompanyTemplates, UploadDocumentInput } from '../../../services/Interfaces/IFolderService';

import { errorToast, successToast } from '../../../utils/toasts';
import { isDelawareJurisdiction } from '../../../utils/jurisdiction';
import VisualizeDocument from './components/VisualiseDocument';
import { CompanyInfoCard } from './components/CompanyInfo';
import { ShareCapitalCard } from './components/ShareCapital';
import { ShareholdersList } from './components/ShareholderList';
import { ButtonApproval } from './components/ButtonApproval';
import { DocumentsList } from './components/DocumentsList';
import { AiOutlineLink } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import { PageHeadline } from '../../../components/atoms';
import { IncorporationAdminPanel } from '../../IncorporationTracking/IncorporationAdminPanel';

export interface DocumentsCompany {
  id: number;
  expire_process: string | null;
  type: string;
  company_documents: CompanyDocuments[];
  shareholders: Shareholder[];
}

interface DocTemplate {
  id: string;
  folderId: string;
  label: string;
  alias: string;
  shareholder_id?: string | number;
}

export const DashboardApprovalDocument: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const api: AxiosInstance = useApi();

  const [isLoading, setIsLoading] = useState(false);
  const [isLanguageRefreshing, setIsLanguageRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [overlayBounds, setOverlayBounds] = useState<{ left: number; top: number; right: number; bottom: number } | null>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [documents, setDocuments] = useState<DocumentsCompany>();
  const [templates, setTemplates] = useState<ICompanyTemplates>();
  const [open, setOpen] = useState(false);
  const [showUploadArea, setShowUploadArea] = useState(false);

  // novo state para Registration Number
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [selectedIbcName, setSelectedIbcName] = useState('');
  const [companyIncorpDate, setCompanyIncorpDate] = useState('');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [completeErrors, setCompleteErrors] = useState<{
    registrationNumber?: string;
    selectedIbcName?: string;
    companyIncorpDate?: string;
    certificateFile?: string;
  }>({});


  const [fileUploads, setFileUploads] = useState<Record<string, File[]>>({});
  const [incorpDates, setIncorpDates] = useState<Record<string, string>>({});
  const [showMessage, setShowMessage] = useState(false);
  const [documentsCertificates, setDocumentsCertificates] = useState<Documents[]>();
  
  // Estados para upload separado de certificados
  const [uploadingTemplates, setUploadingTemplates] = useState<Record<string, boolean>>({});
  const [uploadedTemplates, setUploadedTemplates] = useState<Record<string, boolean>>({});
  
  // Estados para controle de registro da empresa
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [registrationData, setRegistrationData] = useState<{
    registrationNumber: string;
    selectedIbcName: string;
    companyIncorpDate: string;
  } | null>(null);

  const {
    shareholderSlug,
    setShareholderSlug,
    documentSlug,
    setDocumentSlug,
    documentUrl,
    setDocumentUrl
  } = useDialogsStore();

  const theDocumentsAreApprovedForButtonRelease = () => {
    if (!documents || documents.shareholders.length === 0) {
      return true;
    }
    const hasUnapprovedShareholderDocuments = documents.shareholders.some((s: Shareholder) =>
      s.documents.some(d => d.status !== 'approved')
    );
    const hasUnapprovedShareholderAddresses = documents.shareholders.some((s: Shareholder) =>
      s.address.some(a => a.status !== 'approved')
    );
    const hasUnapprovedShareholderPassports = documents.shareholders.some((s: Shareholder) =>
      s.passports.some(p => p.status !== 'approved')
    );
    const hasUnapprovedNewCompanyDocuments =
      Array.isArray(documents.company_documents) &&
      documents.company_documents.some((newCompanyDoc: any) => newCompanyDoc.document.status !== 'approved');
    const isCompanyApproved = companyInfo?.status === 'approved';

    return (
      isCompanyApproved ||
      hasUnapprovedShareholderDocuments ||
      hasUnapprovedShareholderAddresses ||
      hasUnapprovedShareholderPassports ||
      hasUnapprovedNewCompanyDocuments
    );
  };

  const getData = async (options?: { showSpinner?: boolean }) => {
    const { showSpinner = true } = options ?? {};

    try {
      if (showSpinner) {
        setIsLoading(true);
      }

      const { data } = await ManagementService.getCompanyInfo(api, slug!, {
        headers: {
          'Content-Language': i18n.language.toLowerCase(),
          Authorization: `Bearer ${token}`
        }
      });
      const docs = await ManagementService.getCompanyDocuments(api, data.id, {
        headers: {
          'Content-Language': i18n.language.toLowerCase(),
          Authorization: `Bearer ${token}`
        }
      });
      const responseTemplates = await FolderService.getCompanyTemplatesDocuments(api, {
        headers: {
          'Content-Language': i18n.language.toLowerCase(),
          Authorization: `Bearer ${token}`
        }
      });

      setCompanyInfo(data);
      setDocuments(docs.data);
      setTemplates(responseTemplates);
      
      // Debug: verificar estrutura dos templates do backend
      console.log('🔍 Templates from backend:', responseTemplates);

      // Verificar se o registro da empresa já foi completado
      if (data.registration_number && data.incorporation_date) {
        setRegistrationCompleted(true);
        setRegistrationData({
          registrationNumber: String(data.registration_number),
          selectedIbcName: data.name || '',
          companyIncorpDate: String(data.incorporation_date)
        });
      }

      // Verificar quais certificados já foram enviados
      await checkUploadedCertificates(data.id);
      
    } catch (e: any) {
      errorToast(errorMessageAdapter(e));
    } finally {
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!slug || !token) {
      return;
    }
    void getData();
  }, [slug, token]);

  useEffect(() => {
    if (!slug || !token) {
      return;
    }

    setIsLanguageRefreshing(true);
    const timeout = setTimeout(() => {
      void getData({ showSpinner: false }).finally(() => {
        setIsLanguageRefreshing(false);
      });
    }, 700);

    return () => {
      clearTimeout(timeout);
      setIsLanguageRefreshing(false);
    };
  }, [i18n.language, slug, token]);

  const nonDirectorShareholders: Shareholder[] =
    companyInfo?.shareholders?.filter((sh: Shareholder) => !sh.is_director) || [];

  // Verificar se a empresa já está aprovada
  const isCompanyApproved = companyInfo?.status === 'approved';

  // Encontrar o template de Certificate of Shares pelo alias (fallback por nome)
  const certTemplate = templates?.templates.find(
    t => t.alias === 'CERTIFICATE_SHARES' || t.name === 'Certificate of Shares'
  );
  const baseTemplates = templates?.templates.filter(t => t !== certTemplate) || [];
  
  // Debug: verificar se o template foi encontrado
  console.log('🔍 Certificate template found:', certTemplate);
  console.log('🔍 All templates:', templates?.templates.map(t => ({ id: t.id, name: t.name })));

  const mappedBase: DocTemplate[] = baseTemplates.map(t => ({
    id: t.id,
    folderId: t.instance_id,
    label: t.name,
    alias: t.alias || '',
  }));

  const dynamicShares: DocTemplate[] = certTemplate
    ? nonDirectorShareholders.map(sh => ({
      id: certTemplate.id,
      folderId: certTemplate.instance_id,
      label: `${certTemplate.name} – ${sh.name}`,
      alias: 'CERTIFICATE_SHARES',
      shareholder_id: sh.id
    }))
    : [];

  const allDocumentTemplates: DocTemplate[] = [
    ...mappedBase,
    ...dynamicShares,
  ];

  // Documentos obrigatórios para aprovação (excluindo SEAL_COPY)
  const requiredDocumentTemplates = allDocumentTemplates.filter(template => 
    template.alias !== 'SEAL_COPY'
  );

  const handleShowUpload = () => {
    const initUploads = Object.fromEntries(
      allDocumentTemplates.map(t => [t.label, [] as File[]])
    );
    const initDates = Object.fromEntries(
      allDocumentTemplates.map(t => [t.label, ''])
    );
    setFileUploads(initUploads);
    setIncorpDates(initDates);
    
    // Inicializa dados de registro
    if (registrationData) {
      setRegistrationNumber(registrationData.registrationNumber);
      setSelectedIbcName(registrationData.selectedIbcName);
      setCompanyIncorpDate(registrationData.companyIncorpDate);
    } else {
      setRegistrationNumber(companyInfo?.registration_number || '');
    }
    
    setShowUploadArea(true);
  };

  const handleFileChange = (label: string, files: FileList | null) => {
    if (!files) return;
    setFileUploads(prev => ({ ...prev, [label]: Array.from(files) }));
    setShowMessage(false);
  };

  // Função para obter o shareholder_id a partir do nome no template
  const getShareholderIdFromTemplate = (template: DocTemplate): string | number | undefined => {
    // Só retorna shareholder_id para Certificate of Shares (verificar pelo ID do template)
    if (!certTemplate || template.id !== certTemplate.id || !companyInfo?.shareholders) {
      return undefined;
    }
    
    // Extrair o nome do acionista do label do template
    const shareholderName = template.label.replace(`${certTemplate.name} – `, '');
    
    // Encontrar o acionista pelo nome
    const shareholder = companyInfo.shareholders.find((sh: any) => sh.name === shareholderName);
    
    console.log('🔍 Getting shareholder ID:', {
      templateLabel: template.label,
      templateId: template.id,
      extractedName: shareholderName,
      foundShareholder: shareholder,
      shareholderId: shareholder?.id
    });
    
    return shareholder?.id;
  };

  // Função para upload individual de certificado
  const handleIndividualUpload = async (template: DocTemplate) => {
    console.log('***template', template);
    const files = fileUploads[template.label];
    const issueDate = incorpDates[template.label];
    
    console.log('🔍 Upload validation:', {
      templateLabel: template.label,
      hasFiles: !!(files && files.length > 0),
      filesCount: files?.length || 0,
      hasIssueDate: !!issueDate,
      issueDate: issueDate
    });
    
    if (!files || files.length === 0) {
      errorToast(t('labels.selectFileFirst') || 'Please select a file first');
      return;
    }

    if (!issueDate) {
      errorToast(t('labels.selectDateFirst') || 'Please select an issue date first');
      return;
    }

    try {
      setUploadingTemplates(prev => ({ ...prev, [template.label]: true }));

      const alias = template.alias;

      // Obter o shareholder_id correto a partir do companyInfo
      const shareholderId = getShareholderIdFromTemplate(template);

      console.log('📤 Upload payload with shareholder ID:', {
        templateLabel: template.label,
        shareholderId: shareholderId,
        filesCount: files.length,
        alias: alias,
        isCertificateOfCompliance: template.label === 'Certificate of Compliance'
      });

      const uploadPayload: UploadDocumentInput[] = files.map(file => ({
        company_id: companyInfo.id,
        template: 'CERTIFICATES',
        document: file,
        customer_can_view: true,
        customer_can_manage: true,
        document_template_alias: alias,
        user_id: shareholderId ?? '',
        shareholder_id: shareholderId ?? '',
        issue_date: formatDate(issueDate)
      }));

      await FolderService.agentUploadMultipleDocuments(api, uploadPayload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
      });

      // Marcar como enviado
      setUploadedTemplates(prev => ({ ...prev, [template.label]: true }));
      
      // Limpar arquivos do template específico
      setFileUploads(prev => ({ ...prev, [template.label]: [] }));
      setIncorpDates(prev => ({ ...prev, [template.label]: '' }));

      successToast(t('messages.documentUploadedSuccessfully') || 'Document uploaded successfully');
      
      // Recarregar dados para mostrar o documento enviado
      await getData();
      
    } catch (error) {
      console.error('Error uploading individual document:', error);
      errorToast(errorMessageAdapter(error));
    } finally {
      setUploadingTemplates(prev => ({ ...prev, [template.label]: false }));
    }
  };

  function formatDate(date: string | Date): string {
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    return new Date(date).toISOString().split('T')[0];
  }

  const handleSendRegistrationData = async () => {
    const errors: typeof completeErrors = {};

    if (!registrationNumber.trim()) {
      errors.registrationNumber = t('validations.required') || 'Este campo é obrigatório';
    }
    if (!selectedIbcName.trim()) {
      errors.selectedIbcName = t('validations.required') || 'Este campo é obrigatório';
    }
    if (!companyIncorpDate.trim()) {
      errors.companyIncorpDate = t('validations.required') || 'Este campo é obrigatório';
    }

    if (Object.keys(errors).length > 0) {
      setCompleteErrors(errors);
      return;
    }

    try {
      setCompleteLoading(true);
      setCompleteErrors({});
      
      await ManagementService.completeRegister(api, {
        registration_number: registrationNumber,
        name: selectedIbcName,
        incorporation_date: companyIncorpDate.replace(/-/g, '/'),
        company_id: companyInfo?.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Language': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      setRegistrationCompleted(true);
      setRegistrationData({
        registrationNumber,
        selectedIbcName,
        companyIncorpDate
      });
      
      successToast(t('messages.registrationDataSent') || 'Registration data sent successfully');
    } catch (e: any) {
      errorToast(errorMessageAdapter(e));
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleCompleteRegister = async () => {
    try {
      // 1) Só envia dados de registro se ainda não foram enviados
      if (!registrationCompleted) {
        await ManagementService.completeRegister(
          api,
          {
            company_id: companyInfo.id,
            registration_number: registrationNumber,
            incorporation_date: companyIncorpDate.replace(/-/g, '/'),
            name: selectedIbcName
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Language': i18n.language.toLowerCase()
            }
          }
        );
        
        // Marcar como completado
        setRegistrationCompleted(true);
        setRegistrationData({
          registrationNumber,
          selectedIbcName,
          companyIncorpDate
        });
      }

      // 2) Monta payload apenas para documentos obrigatórios que ainda não foram enviados
      const remainingTemplates = requiredDocumentTemplates.filter(t => !uploadedTemplates[t.label]);
      const uploadPayload: UploadDocumentInput[] = remainingTemplates
        .flatMap(template => {
          const files = fileUploads[template.label] || [];
          const issueDate = incorpDates[template.label];
            const alias = template.alias;

          // Obter o shareholder_id correto a partir do companyInfo
          const shareholderId = getShareholderIdFromTemplate(template);

          return files.map(file => ({
            company_id: companyInfo.id,
            template: 'CERTIFICATES',
            document: file,
            customer_can_view: true,
            customer_can_manage: true,
            document_template_alias: alias,
            user_id: shareholderId ?? '',
            shareholder_id: shareholderId ?? '',
            issue_date: issueDate ? formatDate(issueDate) : undefined
          }));
        });

      // Só faz upload se há documentos para enviar
      if (uploadPayload.length > 0) {
        await FolderService.agentUploadMultipleDocuments(api, uploadPayload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
        });
      }

      // 3) Aprova empresa
      await ManagementService.approveCompany(api, companyInfo.slug, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
      });

      successToast(t('messages.msgCompanyAproved'));
      await getData();
    } catch (e: any) {
      errorToast(errorMessageAdapter(e));
    }
  };

  const fetchFilesCertificates = async () => {
    try {
      const response = await FolderService.getCompanyDocumentsInFolderAlias(
        api, slug, 'CERTIFICATES', { headers: { 'Content-Language': i18n.language.toLowerCase(), Authorization: `Bearer ${token}` } }
      );
      if (response.documents.length > 0) {
        setDocumentsCertificates(response.documents);
        setShowUploadArea(true);
      }
    } catch (e: any) {
      errorToast(errorMessageAdapter(e));
    }
  }

  // Verificar quais certificados já foram enviados
  const checkUploadedCertificates = async (companyId: number) => {
    try {
      console.log('🔍 Checking uploaded certificates...');
      const response = await FolderService.getCompanyDocumentsInFolderAlias(
        api, slug, 'CERTIFICATES', { headers: { 'Content-Language': i18n.language.toLowerCase(), Authorization: `Bearer ${token}` } }
      );
      
      console.log('📄 Documents response:', response.documents);
      
      if (response.documents.length > 0) {
        setDocumentsCertificates(response.documents);
        
        // Mapear documentos enviados para templates usando a mesma lógica de ordem
        const uploadedTemplatesMap: Record<string, boolean> = {};
        
        // Filtrar acionistas não-diretores (mesma lógica usada para criar templates)
        const nonDirectorShareholders: Shareholder[] =
          companyInfo?.shareholders?.filter((sh: Shareholder) => !sh.is_director) || [];
        
        console.log('👥 Non-director shareholders:', nonDirectorShareholders);
        
        // Separar documentos por tipo usando IDs dos templates
        const certificateOfSharesDocs = response.documents.filter((doc: any) => 
          certTemplate && doc.template?.id === certTemplate.id
        );
        const otherDocs = response.documents.filter((doc: any) => 
          !certTemplate || doc.template?.id !== certTemplate.id
        );
        
        console.log('📋 Certificate of Shares documents:', certificateOfSharesDocs);
        console.log('📋 Other documents:', otherDocs);
        
        // Mapear outros documentos pelos IDs dos templates
        otherDocs.forEach((doc: any) => {
          const templateId = doc.template?.id;
          if (templateId) {
            // Encontrar o template correspondente para obter o nome traduzido
            const template = templates?.templates.find(t => t.id === templateId);
            if (template) {
              console.log('📄 Other template:', template.name, 'ID:', templateId);
              uploadedTemplatesMap[template.name] = true;
            }
          }
        });
        
        // Mapear Certificate of Shares - tentar por user_id primeiro, depois por ordem
        const usedShareholderIds = new Set();
        
        certificateOfSharesDocs.forEach((doc: any, index: number) => {
          let shareholder = null;
          let templateLabel = '';
          
          // Primeiro, tentar encontrar por user_id se disponível
          if (doc.user_id) {
            shareholder = nonDirectorShareholders.find((sh: any) => sh.id === doc.user_id);
            if (shareholder && !usedShareholderIds.has(shareholder.id)) {
              templateLabel = `${certTemplate?.name} – ${shareholder.name}`;
              usedShareholderIds.add(shareholder.id);
              console.log(`✅ Mapping certificate by user_id ${doc.user_id} to shareholder ${shareholder.name}:`, templateLabel);
            }
          }
          
          // Se não encontrou por user_id, usar ordem como fallback
          if (!shareholder && index < nonDirectorShareholders.length) {
            // Encontrar o primeiro acionista não usado
            const availableShareholders = nonDirectorShareholders.filter(sh => !usedShareholderIds.has(sh.id));
            if (availableShareholders.length > 0) {
              shareholder = availableShareholders[0];
              templateLabel = `${certTemplate?.name} – ${shareholder.name}`;
              usedShareholderIds.add(shareholder.id);
              console.log(`✅ Mapping certificate by order ${index} to shareholder ${shareholder.name}:`, templateLabel);
            }
          }
          
          if (shareholder && templateLabel) {
            uploadedTemplatesMap[templateLabel] = true;
          } else {
            console.log('⚠️ Could not map certificate, using generic');
            if (certTemplate) {
              uploadedTemplatesMap[certTemplate.name] = true;
            }
          }
        });
        
        console.log('🗂️ Uploaded templates map:', uploadedTemplatesMap);
        setUploadedTemplates(uploadedTemplatesMap);
        setShowUploadArea(true);
      }
    } catch (e: any) {
      console.error('❌ Error checking uploaded certificates:', e);
    }
  };
  // const theDocumentsAreApprovedForButtonRelease = () => {
  //   // Verifica se os documentos ou shareholders estão vazios
  //   if (!documents || documents.shareholders.length === 0) {
  //     return true;
  //   }

  //   // Verifica se algum documento de shareholders, endereço ou passaporte não está aprovado
  //   const hasUnapprovedShareholderDocuments = documents.shareholders.some((shareholder: Shareholder) =>
  //     shareholder.documents.some((doc: any) => doc.status !== 'approved')
  //   );
  //   const hasUnapprovedShareholderAddresses = documents.shareholders.some((shareholder: Shareholder) =>
  //     shareholder.address.some((addr: any) => addr.status !== 'approved')
  //   );
  //   const hasUnapprovedShareholderPassports = documents.shareholders.some((shareholder: Shareholder) =>
  //     shareholder.passports.some((passport: any) => passport.status !== 'approved')
  //   );

  //   // Verifica o novo objeto company_documents (Company Transfer)
  //   const hasUnapprovedNewCompanyDocuments =
  //     Array.isArray(documents.company_documents) &&
  //     documents.company_documents.some((newCompanyDoc: any) => newCompanyDoc.document.status !== 'approved');

  //   // Verifica se a empresa já está aprovada
  //   const isCompanyApproved = companyInfo?.status === 'approved';

  //   // Desabilita o botão se qualquer uma das condições for verdadeira
  //   return (
  //     isCompanyApproved ||
  //     hasUnapprovedShareholderDocuments ||
  //     hasUnapprovedShareholderAddresses ||
  //     hasUnapprovedShareholderPassports ||
  //     hasUnapprovedNewCompanyDocuments
  //   );
  // };

  useEffect(() => { fetchFilesCertificates(); }, []);

  const changeOpen = (shSlug: string, docSlug: string, docUrl: string) => {
    setOpen(true);
    setShareholderSlug(shSlug);
    setDocumentSlug(docSlug);
    setDocumentUrl(docUrl);
  };

  async function downloadPdf(url: string, filename: string) {
    const response = await api.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  console.log('*******', allDocumentTemplates);
  
  const handleDocumentApproved = (_typeDialog?: string) => {
    void getData();
  };

  const updateOverlayBounds = useCallback(() => {
    if (!containerRef.current) {
      setOverlayBounds(null);
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    setOverlayBounds({
      left: Math.max(rect.left, 0),
      top: Math.max(rect.top, 0),
      right: Math.max(window.innerWidth - rect.right, 0),
      bottom: Math.max(window.innerHeight - rect.bottom, 0)
    });
  }, []);

  useEffect(() => {
    if (!(isLoading || isLanguageRefreshing)) {
      setOverlayBounds(null);
      return;
    }

    updateOverlayBounds();

    window.addEventListener('resize', updateOverlayBounds);
    window.addEventListener('scroll', updateOverlayBounds, true);

    return () => {
      window.removeEventListener('resize', updateOverlayBounds);
      window.removeEventListener('scroll', updateOverlayBounds, true);
    };
  }, [isLoading, isLanguageRefreshing, updateOverlayBounds]);

  return (
    <div ref={containerRef} className="relative min-h-screen bg-gray-50 p-6">
      {overlayBounds && (
        <div
          className="fixed z-50 flex items-center justify-center bg-white"
          style={{
            left: overlayBounds.left,
            top: overlayBounds.top,
            right: overlayBounds.right,
            bottom: overlayBounds.bottom
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E1E9F5] border-t-[#0C1FAA]" />
            <span className="text-xs font-medium text-[#6A7A90]">
              {t('labels.lblLoading') || 'Loading...'}
            </span>
          </div>
        </div>
      )}
      {/* Header padrão */}
      <div className="mb-8">
        <PageHeadline
          title={t('others.othApproveCompany')}
          subtitle={companyInfo?.ibc_primary || companyInfo?.name || '---'}
        />
      </div>

      {/* Grid layout para organizar as seções */}
      <div className="grid gap-6">
        <IncorporationAdminPanel company={companyInfo} />

        {/* Informações da Empresa */}
        <section className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6">
            <CompanyInfoCard companyInfo={companyInfo} />
          </div>
        </section>

        {/* Capital Social */}
        <section className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {t('others.othShareCapital')}
            </h2>
          </div>
          <div className="p-6">
            <ShareCapitalCard companyInfo={companyInfo} />
          </div>
        </section>
      </div>

      {/* Diretores e Acionistas */}
      <div className="mt-6">
        <ShareholdersList
          shareholders={companyInfo?.shareholders}
          isDelaware={isDelawareJurisdiction(companyInfo?.jurisdiction)}
        />
      </div>

      {/* Documentos da Empresa */}
      <section className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('labels.lblCompanyDocuments')}
          </h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <DocumentsList documents={documents} changeOpen={changeOpen} />
          </div>
        </div>
      </section>

      {!showUploadArea && (
        <div className="mt-8 flex justify-center">
          <ButtonApproval
            label={t('others.othStartApproval')}
            onClick={handleShowUpload}
            isDisabled={theDocumentsAreApprovedForButtonRelease()}
          />
        </div>
      )}

      {showUploadArea && (
        <section className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {t('labels.lblUploadCertificates')}
              </h2>
              {companyInfo && (
                <Link 
                  to={`/dashboard/documents-status/${companyInfo.id}`} 
                  className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg px-4 py-2 text-sm font-medium text-indigo-700 transition-colors"
                >
                  <AiOutlineLink className="w-4 h-4" /> 
                  {t('titles.ttlVerifySignatures')}
                </Link>
              )}
            </div>
          </div>
          <div className="p-6">
          {/* Seção de status de registro - aparece sempre */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t('companyTransfer.othCompleteCompanyRegistration')}
              </h3>
              {/* Status indicator */}
              <div className="flex items-center gap-2">
                {registrationCompleted ? (
                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium">{t('labels.completed') || 'Concluído'}</span>
                  </div>
                ) : (
                  <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium">{t('labels.pending') || 'Pendente'}</span>
                  </div>
                )}
              </div>
            </div>
            
            {registrationCompleted ? (
              /* Mostrar dados completados de forma elegante */
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('companyTransfer.othNumberRegisterCompany')}
                  </label>
                  <p className="text-lg font-semibold text-gray-800">
                    {registrationData?.registrationNumber || companyInfo?.registration_number}
                  </p>
                </div>
                <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('others.othCompanyName')}
                  </label>
                  <p className="text-lg font-semibold text-gray-800">
                    {registrationData?.selectedIbcName || companyInfo?.name}
                  </p>
                </div>
                <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('labels.lblIncorporationDate')}
                  </label>
                  <p className="text-lg font-semibold text-gray-800">
                    {registrationData?.companyIncorpDate || companyInfo?.incorporation_date}
                  </p>
                </div>
              </div>
            ) : (
              /* Mostrar formulário de registro */
              <div className="grid grid-cols-3 gap-4">
                {/* registration number */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    {t('companyTransfer.othNumberRegisterCompany')}
                  </label>
                  <input
                    type="text"
                    value={registrationNumber}
                    disabled={registrationCompleted}
                    onChange={e => {
                      setRegistrationNumber(e.target.value);
                      setCompleteErrors(prev => ({ ...prev, registrationNumber: undefined }));
                    }}
                    className={`mt-1 w-full rounded border px-3 py-2 ${
                      completeErrors.registrationNumber ? 'border-red-500' : 
                      registrationCompleted ? 'border-green-300 bg-green-50' : 'border-gray-300'
                    }`}
                  />
                  {completeErrors.registrationNumber && (
                    <p className="text-xs text-red-500 mt-1">
                      {completeErrors.registrationNumber}
                    </p>
                  )}
                </div>

                {/* select IBC */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    {t('others.othCompanyName')}
                  </label>
                  <select
                    value={selectedIbcName}
                    disabled={registrationCompleted}
                    onChange={e => {
                      setSelectedIbcName(e.target.value);
                      setCompleteErrors(prev => ({ ...prev, selectedIbcName: undefined }));
                    }}
                    className={`mt-1 w-full rounded border px-3 py-2 ${
                      completeErrors.selectedIbcName ? 'border-red-500' : 
                      registrationCompleted ? 'border-green-300 bg-green-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">{t('fields.fldSelectYourOption')}</option>
                    <option value={companyInfo?.ibc_primary}>
                      {companyInfo?.ibc_primary}
                    </option>
                    <option value={companyInfo?.ibc_secondary}>
                      {companyInfo?.ibc_secondary}
                    </option>
                    <option value={companyInfo?.ibc_third}>
                      {companyInfo?.ibc_third}
                    </option>
                  </select>
                  {completeErrors.selectedIbcName && (
                    <p className="text-xs text-red-500 mt-1">
                      {completeErrors.selectedIbcName}
                    </p>
                  )}
                </div>

                {/* incorporation date */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    {t('labels.lblIncorporationDate')}
                  </label>
                  <input
                    type="date"
                    value={companyIncorpDate}
                    disabled={registrationCompleted}
                    onChange={e => {
                      setCompanyIncorpDate(e.target.value);
                      setCompleteErrors(prev => ({ ...prev, companyIncorpDate: undefined }));
                    }}
                    className={`mt-1 w-full rounded border px-3 py-2 ${
                      completeErrors.companyIncorpDate ? 'border-red-500' : 
                      registrationCompleted ? 'border-green-300 bg-green-50' : 'border-gray-300'
                    }`}
                  />
                  {completeErrors.companyIncorpDate && (
                    <p className="text-xs text-red-500 mt-1">
                      {completeErrors.companyIncorpDate}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Botão para enviar dados de registro - só aparece se não foi completado */}
            {!registrationCompleted && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSendRegistrationData}
                  disabled={completeLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {completeLoading 
                    ? t('labels.sending') || 'Sending...' 
                    : t('labels.sendRegistrationData') || 'Send Registration Data'
                  }
                </button>
              </div>
            )}
          </div>




          {/* Separar templates em enviados e pendentes */}
          {(() => {
            const uploadedTemplates = allDocumentTemplates.filter(template => {
              if (certTemplate && template.id === certTemplate.id) {
                const certificateOfSharesDocs = documentsCertificates?.filter((doc: any) => certTemplate && doc.template?.id === certTemplate.id) || [];
                const nonDirectorShareholders = companyInfo?.shareholders?.filter((sh: Shareholder) => !sh.is_director) || [];
                
                // Buscar por user_id primeiro
                let uploadedDoc = certificateOfSharesDocs.find((doc: any) => 
                  doc.user_id === template.shareholder_id
                );
                
                // Se não encontrou por user_id, usar ordem como fallback
                if (!uploadedDoc) {
                  const shareholderIndex = nonDirectorShareholders.findIndex((sh: any) => sh.id === template.shareholder_id);
                  if (shareholderIndex >= 0 && shareholderIndex < certificateOfSharesDocs.length) {
                    const usedByUserId = certificateOfSharesDocs.some((doc: any) => 
                      doc.user_id && nonDirectorShareholders.findIndex((sh: any) => sh.id === doc.user_id) === shareholderIndex
                    );
                    if (!usedByUserId) {
                      uploadedDoc = certificateOfSharesDocs[shareholderIndex];
                    }
                  }
                }
                
                return !!uploadedDoc;
              } else {
                const documents = documentsCertificates?.filter((doc: any) => doc.template?.id === template.id) || [];
                return documents.length > 0;
              }
            });

            const pendingTemplates = allDocumentTemplates.filter(template => !uploadedTemplates.includes(template));

            return (
              <div>
                {/* Cards para documentos pendentes */}
                <div className="grid grid-cols-2 gap-4 items-start">
                  {pendingTemplates.map((template, templateIndex) => {
                    return (
                      <div key={template.label} className="flex flex-col bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-medium text-gray-600">
                              {template.label}
                            </span>
                            {template.alias === 'SEAL_COPY' && (
                              <div className="group relative">
                                <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex items-center justify-center cursor-help hover:border-gray-800 transition-colors">
                                  <span className="text-gray-600 text-sm font-bold">?</span>
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                                  {t('labels.sealCopyNotRequired') || 'This document is not required for approval'}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <input
                              type="date"
                              value={incorpDates[template.label]}
                              onChange={e => setIncorpDates(prev => ({ ...prev, [template.label]: e.target.value }))}
                              className="w-full border rounded px-3 py-2 mb-3"
                            />
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-500 mb-1">
                                {t('labels.lblPushYourDocument')}
                              </label>
                              <div className="relative flex h-28 w-full items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors">
                                <input
                                  type="file"
                                  accept=".pdf"
                                  onChange={e => handleFileChange(template.label, e.target.files)}
                                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                                {fileUploads[template.label] && fileUploads[template.label].length > 0 ? (
                                  <div className="flex flex-col items-center text-center px-4">
                                    <svg className="h-6 w-6 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm font-medium text-green-600 mb-1">
                                      {t('labels.fileSelected') || 'File selected'}
                                    </p>
                                    <p className="text-xs text-gray-600 truncate max-w-full">
                                      {fileUploads[template.label][0].name}
                                    </p>
                                    {fileUploads[template.label].length > 1 && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        +{fileUploads[template.label].length - 1} {t('labels.moreFiles') || 'more files'}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <p className="mt-2 text-sm text-gray-500">
                                      {t('labels.lblClickToUpload')}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <button
                              onClick={() => handleIndividualUpload(template)}
                              disabled={!fileUploads[template.label] || fileUploads[template.label].length === 0}
                              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {t('labels.lblUpload')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Seção de documentos enviados */}
                {uploadedTemplates.length > 0 && (
                  <div className="mt-8 bg-white rounded-lg p-6">
                    {/* Upload Status Section */}
                    {(() => {
                      const requiredDocumentsUploaded = requiredDocumentTemplates.filter(template => 
                        uploadedTemplates.includes(template)
                      ).length === requiredDocumentTemplates.length;
                      const allComplete = requiredDocumentsUploaded && registrationCompleted;
                      
                      if (allComplete) {
                        return null;
                      }
                      
                      const uploadedCount = requiredDocumentTemplates.filter(template => 
                        uploadedTemplates.includes(template)
                      ).length;
                      const totalRequired = requiredDocumentTemplates.length;
                      const progressPercentage = (uploadedCount / totalRequired) * 100;
                      
                      return (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">
                            {t('labels.uploadStatus') || 'Upload Status'}
                          </h4>
                          <div className="text-sm text-blue-700 mb-3">
                            {uploadedCount} / {totalRequired} {t('labels.requiredDocumentsUploaded') || 'required documents uploaded'}
                            {uploadedTemplates.length > totalRequired && (
                              <span className="text-gray-500 ml-2">
                                (+{uploadedTemplates.length - totalRequired} optional)
                              </span>
                            )}
                          </div>
                          {/* Progress Bar */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                                style={{ width: `${progressPercentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-700 min-w-[3rem] text-right">
                              {Math.round(progressPercentage)}%
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Uploaded Documents Section */}
                    {(() => {
                      const requiredDocumentsUploaded = requiredDocumentTemplates.filter(template => 
                        uploadedTemplates.includes(template)
                      ).length === requiredDocumentTemplates.length;
                      const allComplete = requiredDocumentsUploaded && registrationCompleted;
                      
                      if (allComplete) {
                        return null;
                      }
                      
                      return (
                        <h4 className="text-lg font-semibold text-gray-700 mb-4">
                          {t('labels.uploadedDocuments') || 'Uploaded Documents'}
                        </h4>
                      );
                    })()}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {uploadedTemplates.map((template) => {
                        let uploadedDoc: Documents | null = null;
                        
                        if (certTemplate && template.id === certTemplate.id) {
                          const certificateOfSharesDocs = documentsCertificates?.filter((doc: any) => certTemplate && doc.template?.id === certTemplate.id) || [];
                          const nonDirectorShareholders = companyInfo?.shareholders?.filter((sh: Shareholder) => !sh.is_director) || [];
                          
                          // Buscar por user_id primeiro
                          uploadedDoc = certificateOfSharesDocs.find((doc: any) => 
                            doc.user_id === template.shareholder_id
                          ) || null;
                          
                          // Se não encontrou por user_id, usar ordem como fallback
                          if (!uploadedDoc) {
                            const shareholderIndex = nonDirectorShareholders.findIndex((sh: any) => sh.id === template.shareholder_id);
                            if (shareholderIndex >= 0 && shareholderIndex < certificateOfSharesDocs.length) {
                              const usedByUserId = certificateOfSharesDocs.some((doc: any) => 
                                doc.user_id && nonDirectorShareholders.findIndex((sh: any) => sh.id === doc.user_id) === shareholderIndex
                              );
                              if (!usedByUserId) {
                                uploadedDoc = certificateOfSharesDocs[shareholderIndex];
                              }
                            }
                          }
                        } else {
                          uploadedDoc = documentsCertificates?.find((doc: any) => doc.template?.id === template.id) || null;
                        }

                        return (
                          <div key={template.label} className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-sm font-medium text-green-800">
                                {template.label}
                              </h5>
                              <span className="text-xs text-green-600">✅ {t('labels.uploaded') || 'Uploaded'}</span>
                            </div>
                            {uploadedDoc && (
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-green-600">
                                  {t('labels.issueDate') || 'Issue date'}: {uploadedDoc.issue_date}
                                </div>
                                <button
                                  onClick={() => uploadedDoc && downloadPdf(`https://offshore2go-documents.s3.us-east-2.amazonaws.com/${uploadedDoc.s3path}`, uploadedDoc.name)}
                                  className="flex items-center px-2 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50 transition-colors"
                                >
                                  <FiDownload className="w-3 h-3 mr-1" />
                                  {t('labels.download') || 'Download'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {showMessage && <p className="mt-4 text-sm text-red-500">{t('OnboardingCreateAccountErrorFileSize')}</p>}
              </div>
            );
          })()}

          {showMessage && <p className="mt-4 text-sm text-red-500">{t('OnboardingCreateAccountErrorFileSize')}</p>}

          {/* Status e botão de aprovação - só aparece se não estiver tudo completo */}
          {(() => {
            const uploadedTemplates = allDocumentTemplates.filter(template => {
              if (certTemplate && template.id === certTemplate.id) {
                const certificateOfSharesDocs = documentsCertificates?.filter((doc: any) => certTemplate && doc.template?.id === certTemplate.id) || [];
                const nonDirectorShareholders = companyInfo?.shareholders?.filter((sh: Shareholder) => !sh.is_director) || [];
                
                let uploadedDoc = certificateOfSharesDocs.find((doc: any) => 
                  doc.user_id === template.shareholder_id
                );
                
                if (!uploadedDoc) {
                  const shareholderIndex = nonDirectorShareholders.findIndex((sh: any) => sh.id === template.shareholder_id);
                  if (shareholderIndex >= 0 && shareholderIndex < certificateOfSharesDocs.length) {
                    const usedByUserId = certificateOfSharesDocs.some((doc: any) => 
                      doc.user_id && nonDirectorShareholders.findIndex((sh: any) => sh.id === doc.user_id) === shareholderIndex
                    );
                    if (!usedByUserId) {
                      uploadedDoc = certificateOfSharesDocs[shareholderIndex];
                    }
                  }
                }
                
                return !!uploadedDoc;
              } else {
                const documents = documentsCertificates?.filter((doc: any) => doc.template?.id === template.id) || [];
                return documents.length > 0;
              }
            });
            
            const requiredDocumentsUploaded = requiredDocumentTemplates.filter(template => 
              uploadedTemplates.includes(template)
            ).length === requiredDocumentTemplates.length;
            const canApprove = requiredDocumentsUploaded && registrationCompleted;
            
            // Se a empresa já estiver aprovada, não mostra nada
            if (isCompanyApproved) {
              return null;
            }
            
            // Se tudo estiver completo, só mostra o botão de aprovação
            if (requiredDocumentsUploaded && registrationCompleted) {
              return (
                <div className="mt-6">
                  <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-700">
                      ✅ {t('labels.allRequiredDocumentsUploaded') || 'All required documents uploaded'} - {t('labels.readyToApprove') || 'Ready to approve company'}
                    </div>
                  </div>
                  <ButtonApproval 
                    label={t('others.othApproveCompany') || 'Approve Company'} 
                    onClick={handleCompleteRegister} 
                    isDisabled={false} 
                  />
                </div>
              );
            }
            
            // Se não estiver tudo completo, só mostra o botão de aprovação
            return (
              <div className="mt-6">
                <ButtonApproval 
                  label={t('others.othApproveCompany') || 'Approve Company'} 
                  onClick={handleCompleteRegister} 
                  isDisabled={!canApprove} 
                />
              </div>
            );
          })()}
          </div>
        </section>
      )}

      <VisualizeDocument
        open={open}
        onOpenChange={setOpen}
        shareholderSlug={shareholderSlug || ''}
        documentSlug={documentSlug || ''}
        documentUrl={documentUrl}
        onDocumentApproved={() => void getData()}
        company={companyInfo}
      />
    </div>
  );
};